// src/components/RootErrorBoundary.js
//
// Phase 1: wraps the whole app so a render-time crash shows a graceful
// Arabic fallback UI instead of a white screen, and reports to Sentry
// (if the DSN is configured in .env).

import React from 'react';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { captureException } from '../services/monitoring';

class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Report to Sentry (no-op if not configured)
    captureException(error, { extra: errorInfo });
    // Keep a dev-time log
    // eslint-disable-next-line no-console
    console.error('[RootErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Container
        maxWidth="sm"
        dir="rtl"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box sx={{ color: 'error.main', mb: 2 }}>
            <ErrorOutline sx={{ fontSize: 64 }} />
          </Box>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            حدث خطأ غير متوقع
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            نعتذر عن هذا الخلل. تم تسجيل المشكلة وسنعمل على إصلاحها.
            يمكنكم إعادة تحميل الصفحة لمتابعة العمل.
          </Typography>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box
              sx={{
                bgcolor: 'grey.100',
                color: 'grey.900',
                p: 2,
                mb: 3,
                borderRadius: 1,
                textAlign: 'left',
                direction: 'ltr',
                fontFamily: 'monospace',
                fontSize: 12,
                overflow: 'auto',
                maxHeight: 200,
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={this.handleReload}
            sx={{ py: 1.25, px: 3 }}
          >
            إعادة تحميل الصفحة
          </Button>
        </Paper>
      </Container>
    );
  }
}

export default RootErrorBoundary;
